import React from 'react';
import { Shield, Eye, Lock, UserCheck, Database, Globe } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Shield className="w-16 h-16 text-gold-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-gray-400 text-lg">
            Your privacy is important to us. Learn how we protect and handle your information.
          </p>
          <p className="text-sm text-gray-500 mt-2">Last updated: NOW</p>
        </div>

        <div className="space-y-8">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
            <div className="flex items-center space-x-3 mb-4">
              <Eye className="w-6 h-6 text-gold-400" />
              <h2 className="text-2xl font-bold text-white">Information We Collect</h2>
            </div>
            <div className="space-y-4 text-gray-300">
              <p>
                At AKIDO™, we collect information to provide you with the best fragrance shopping experience. 
                This includes:
              </p>
              
              <div className="ml-4 space-y-2">
                <p><strong className="text-gold-400">Personal Information:</strong> Name, email address, phone number, and shipping address when you create an account or place an order.</p>
                <p><strong className="text-gold-400">Payment Information:</strong> Credit card details and billing information (processed securely through encrypted payment gateways).</p>
                <p><strong className="text-gold-400">Usage Data:</strong> Information about how you interact with our website, including pages visited, products viewed, and search queries.</p>
                <p><strong className="text-gold-400">Device Information:</strong> Your IP address, browser type, operating system, and device identifiers.</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
            <div className="flex items-center space-x-3 mb-4">
              <Database className="w-6 h-6 text-gold-400" />
              <h2 className="text-2xl font-bold text-white">How We Use Your Information</h2>
            </div>
            <div className="space-y-4 text-gray-300">
              <p>We use your information to:</p>
              
              <ul className="ml-4 space-y-2 list-disc list-inside">
                <li>Process and fulfill your orders</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Send order confirmations, shipping updates, and important account information</li>
                <li>Personalize your shopping experience and recommend products</li>
                <li>Improve our website and services</li>
                <li>Prevent fraud and ensure security</li>
                <li>Send promotional emails (only with your consent)</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
            <div className="flex items-center space-x-3 mb-4">
              <Lock className="w-6 h-6 text-gold-400" />
              <h2 className="text-2xl font-bold text-white">Data Protection & Security</h2>
            </div>
            <div className="space-y-4 text-gray-300">
              <p>
                We implement industry-standard security measures to protect your personal information:
              </p>
              
              <ul className="ml-4 space-y-2 list-disc list-inside">
                <li><strong className="text-gold-400">Encryption:</strong> All sensitive data is encrypted using SSL/TLS protocols</li>
                <li><strong className="text-gold-400">Secure Storage:</strong> Your information is stored on secure servers with restricted access</li>
                <li><strong className="text-gold-400">Payment Security:</strong> We never store your complete credit card information</li>
                <li><strong className="text-gold-400">Regular Audits:</strong> Our security practices are regularly reviewed and updated</li>
                <li><strong className="text-gold-400">Access Control:</strong> Only authorized personnel have access to your data</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
            <div className="flex items-center space-x-3 mb-4">
              <Globe className="w-6 h-6 text-gold-400" />
              <h2 className="text-2xl font-bold text-white">Information Sharing</h2>
            </div>
            <div className="space-y-4 text-gray-300">
              <p>
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              
              <ul className="ml-4 space-y-2 list-disc list-inside">
                <li><strong className="text-gold-400">Service Providers:</strong> With trusted partners who help us operate our business (payment processors, shipping companies)</li>
                <li><strong className="text-gold-400">Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong className="text-gold-400">Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</li>
                <li><strong className="text-gold-400">With Your Consent:</strong> When you explicitly agree to share information</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
            <div className="flex items-center space-x-3 mb-4">
              <UserCheck className="w-6 h-6 text-gold-400" />
              <h2 className="text-2xl font-bold text-white">Your Rights & Choices</h2>
            </div>
            <div className="space-y-4 text-gray-300">
              <p>You have the following rights regarding your personal information:</p>
              
              <ul className="ml-4 space-y-2 list-disc list-inside">
                <li><strong className="text-gold-400">Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong className="text-gold-400">Correction:</strong> Update or correct inaccurate information</li>
                <li><strong className="text-gold-400">Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong className="text-gold-400">Portability:</strong> Obtain your data in a portable format</li>
                <li><strong className="text-gold-400">Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
                <li><strong className="text-gold-400">Restriction:</strong> Request limitations on how we process your information</li>
              </ul>
              
              <p className="mt-4">
                To exercise these rights, please contact us at <span className="text-gold-400">privacy@akido.com</span> or through our customer support.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
            <h2 className="text-2xl font-bold text-white mb-4">Cookies & Tracking</h2>
            <div className="space-y-4 text-gray-300">
              <p>
                We use cookies and similar technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookie preferences through your browser settings.
              </p>
              
              <p>
                <strong className="text-gold-400">Types of cookies we use:</strong>
              </p>
              <ul className="ml-4 space-y-1 list-disc list-inside">
                <li>Essential cookies for website functionality</li>
                <li>Analytics cookies to understand user behavior</li>
                <li>Preference cookies to remember your settings</li>
                <li>Marketing cookies for personalized advertisements</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
            <h2 className="text-2xl font-bold text-white mb-4">Data Retention</h2>
            <div className="space-y-4 text-gray-300">
              <p>
                We retain your personal information only as long as necessary to fulfill the purposes outlined in this privacy policy, comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
              
              <ul className="ml-4 space-y-1 list-disc list-inside mt-4">
                <li><strong className="text-gold-400">Account Information:</strong> Until you delete your account</li>
                <li><strong className="text-gold-400">Order History:</strong> 7 years for tax and legal compliance</li>
                <li><strong className="text-gold-400">Marketing Data:</strong> Until you unsubscribe</li>
                <li><strong className="text-gold-400">Analytics Data:</strong> 2 years for business insights</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
            <h2 className="text-2xl font-bold text-white mb-4">Children's Privacy</h2>
            <div className="space-y-4 text-gray-300">
              <p>
                AKIDO does not knowingly collect personal information from children under 18 years of age. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information promptly.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
            <h2 className="text-2xl font-bold text-white mb-4">Changes to This Policy</h2>
            <div className="space-y-4 text-gray-300">
              <p>
                We may update this privacy policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website and updating the "Last updated" date.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gold-900/20 to-gold-800/20 border border-gold-500/20 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
            <div className="space-y-4 text-gray-300">
              <p>
                If you have any questions, concerns, or requests regarding this privacy policy or how we handle your personal information, please contact us:
              </p>
              
              <div className="space-y-2">
                <p><strong className="text-gold-400">Email:</strong> akido.help@akido.in</p>
                <p><strong className="text-gold-400">Phone:</strong> +91 98765 43210</p>
                <p><strong className="text-gold-400">Address:</strong> AKIDO™ Perfumes, Kashmir, Sopore, India</p>
              </div>
              
              <p className="text-sm mt-4">
                We are committed to addressing your privacy concerns and will respond to your inquiries within 30 days.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}